import type { FilmProject, ProjectGoal, Scene } from "../types";
import { chunkCountForSeconds, formatRuntime, sceneStoryboardIssue } from "./storyboard";

export type MissingInputFixRoute = "characters" | "voices" | "references" | "generate_boards";

export interface ReviewGroup {
  id: string;
  title: string;
  countLabel: string;
  detail: string;
  status: "approved" | "warning" | "needs_attention";
  badge: string;
}

export interface MissingInput {
  id: string;
  label: string;
  kind: string;
  required: boolean;
  sceneId?: string;
  characterName?: string;
  fixRoute?: MissingInputFixRoute;
  focusId?: string;
}

export interface PackageMissingRequired {
  category: "source";
  label: string;
  kind: string;
  reason: string;
  sceneId?: string;
  fixRoute?: MissingInputFixRoute;
  focusId?: string;
}

export interface PlanBeat {
  id: string;
  label: string;
  sceneLabel: string;
  note: string;
  seconds: number;
  readiness: "ready" | "needs_input";
}

export interface PlanAction {
  label: string;
  owner: string;
  status: "ready" | "needs_user" | "blocked";
}

export interface AgentPlanSummary {
  agentName: string;
  goal: ProjectGoal;
  confidence: number;
  objective: string;
  strategy: string;
  why: string;
  beats: PlanBeat[];
  missingInputs: MissingInput[];
  nextActions: PlanAction[];
  approvalGates: string[];
  runtimeSeconds: number;
  chunkSeconds: number;
  chunkCount: number;
}

const goalLabels: Record<ProjectGoal, string> = {
  trailer: "Trailer",
  teaser: "Teaser",
  sizzle_reel: "Sizzle reel",
  proof_of_concept: "Proof of concept",
  previz_animatic: "Pre-viz / animatic",
  concept_scene: "Concept scene",
  short_film: "Short film",
  broll_establishing: "Establishing / B-roll",
  vfx_plate: "VFX plate / set extension",
  social_cutdown: "Social cutdown",
  pitch_reel: "Pitch reel",
  festival_investor: "Festival / investor"
};

export function goalLabel(goal: ProjectGoal): string {
  return goalLabels[goal] || goal;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return asArray(value).filter(isRecord);
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function slugId(value: string, fallback = "item"): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || fallback;
}

function humanizeIdentifier(value: unknown, fallback: string): string {
  const text = asString(value, fallback);
  if (!text.includes("_") && !text.includes("-")) return text;
  return text
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(" ");
}

function planActionFromValue(value: unknown): PlanAction | null {
  if (typeof value === "string" && value.trim()) {
    return { label: value.trim(), owner: "user", status: "ready" };
  }
  if (!isRecord(value)) return null;
  const label = asString(value.label || value.action || value.title);
  if (!label) return null;
  const status = asString(value.status);
  return {
    label,
    owner: asString(value.owner, "user"),
    status: status === "needs_user" || status === "blocked" ? status : "ready"
  };
}

function agentPlanRecord(analysis: Record<string, unknown>): Record<string, unknown> {
  return isRecord(analysis.agentPlan) ? analysis.agentPlan : {};
}

function normalizeFixRoute(value: unknown): MissingInputFixRoute | undefined {
  const route = asString(value);
  if (route === "characters" || route === "voices" || route === "references" || route === "generate_boards") return route;
  return undefined;
}

function missingKindFromGroup(item: Record<string, unknown>): string {
  const kind = asString(item.kind || item.type);
  if (kind) return kind;
  const label = `${asString(item.label)} ${asString(item.icon)}`.toLowerCase();
  if (label.includes("character") || label.includes("card")) return "character_card";
  if (label.includes("voice") || label.includes("audio") || label.includes("mic")) return "voice_audio";
  if (label.includes("reference") || label.includes("location") || label.includes("image")) return "scene_reference";
  if (label.includes("music") || label.includes("score")) return "music";
  if (label.includes("sfx") || label.includes("sound") || label.includes("ambience")) return "sfx";
  if (label.includes("board") || label.includes("storyboard") || label.includes("frame")) return "storyboard_frame";
  return "asset";
}

function routeForMissingKind(kind: string, label = ""): MissingInputFixRoute | undefined {
  const text = `${kind} ${label}`.toLowerCase();
  if (text.includes("character") || text.includes("card")) return "characters";
  if (text.includes("voice") || text.includes("audio") || text.includes("speaker")) return "voices";
  if (text.includes("reference") || text.includes("location") || text.includes("scene")) return "references";
  if (text.includes("board") || text.includes("storyboard") || text.includes("frame")) return "generate_boards";
  return undefined;
}

function requiredForMissingKind(kind: string, label = "", severity = ""): boolean {
  const text = `${kind} ${label} ${severity}`.toLowerCase();
  if (text.includes("optional")) return false;
  return severity === "required"
    || severity === "blocking"
    || text.includes("character")
    || text.includes("card")
    || text.includes("voice")
    || text.includes("reference")
    || text.includes("storyboard")
    || text.includes("board");
}

function focusForMissingInput(
  project: FilmProject | undefined,
  route: MissingInputFixRoute | undefined,
  label: string,
  characterName = "",
  sceneId = ""
): Pick<MissingInput, "characterName" | "focusId" | "sceneId"> {
  if (!project) return { characterName, sceneId };
  const labelKey = label.toLowerCase();
  if (route === "characters" || route === "voices") {
    const character = project.characters.find((item) => (
      item.id === characterName
      || item.name.toLowerCase() === characterName.toLowerCase()
      || labelKey.includes(item.name.toLowerCase())
    ));
    return {
      characterName: character?.name || characterName,
      focusId: character?.id,
      sceneId
    };
  }
  if (route === "references" || route === "generate_boards") {
    const scene = project.scenes.find((item) => (
      item.id === sceneId
      || labelKey.includes(item.slugline.toLowerCase())
      || labelKey.includes(item.location.toLowerCase())
      || labelKey.includes(`scene ${item.number}`)
      || labelKey.includes(`sc ${item.number}`)
    ));
    return {
      characterName,
      focusId: scene?.id,
      sceneId: scene?.id || sceneId
    };
  }
  return { characterName, sceneId };
}

function missingInputFromRecord(item: Record<string, unknown>, index: number, project?: FilmProject): MissingInput {
  const label = asString(item.label || item.name || item.item || item.kind, `Missing input ${index + 1}`);
  const kind = asString(item.kind || item.type || item.category, "asset");
  const severity = asString(item.severity || item.required, "");
  const route = normalizeFixRoute(item.fixRoute) || routeForMissingKind(kind, label);
  const characterName = asString(item.characterName || item.character || item.role || item.speaker);
  const sceneId = asString(item.sceneId || item.scene_id);
  const focus = focusForMissingInput(project, route, label, characterName, sceneId);
  return {
    id: asString(item.id, `${kind}-${slugId(label)}-${index + 1}`),
    label,
    kind,
    required: item.required === true || requiredForMissingKind(kind, label, severity),
    sceneId: focus.sceneId,
    characterName: focus.characterName,
    fixRoute: route,
    focusId: asString(item.focusId, focus.focusId)
  };
}

function missingInputsFromAssetGroup(item: Record<string, unknown>, groupIndex: number, project?: FilmProject): MissingInput[] {
  const kind = missingKindFromGroup(item);
  const groupLabel = asString(item.label || item.name || kind, `Missing source ${groupIndex + 1}`);
  const route = routeForMissingKind(kind, groupLabel);
  const severity = asString(item.severity || item.required, "");
  const names = asArray(item.items).map((value) => asString(value)).filter(Boolean);
  const itemLabels = names.length ? names : Number(item.count || 0) > 0 ? [groupLabel] : [];
  return itemLabels.map((name, itemIndex) => {
    const friendlyLabel = name === groupLabel
      ? groupLabel
      : kind === "character_card"
        ? `${name} character card`
        : kind === "voice_audio"
          ? `${name} voice`
          : kind === "scene_reference"
            ? `${name} scene reference`
            : name;
    const focus = focusForMissingInput(project, route, friendlyLabel, route === "characters" || route === "voices" ? name : "", "");
    return {
      id: `${kind}-${slugId(name)}-${groupIndex + 1}-${itemIndex + 1}`,
      label: friendlyLabel,
      kind,
      required: requiredForMissingKind(kind, friendlyLabel, severity),
      sceneId: focus.sceneId,
      characterName: focus.characterName,
      fixRoute: route,
      focusId: focus.focusId
    };
  });
}

function rawMissingInputs(analysis: Record<string, unknown>, project?: FilmProject): MissingInput[] {
  const agentPlan = agentPlanRecord(analysis);
  const fromAgent = asRecordArray(agentPlan.missingInputs);
  const fromAssets = asRecordArray(analysis.missingAssets);
  if (fromAgent.length) return fromAgent.map((item, index) => missingInputFromRecord(item, index, project));
  return fromAssets.flatMap((item, index) => missingInputsFromAssetGroup(item, index, project));
}

function sceneLabelForId(project: FilmProject, sceneId: string): string {
  const scene = project.scenes.find((item) =>
    item.id === sceneId
    || `scene_${item.number}` === sceneId
    || `scene_${String(item.number).padStart(2, "0")}` === sceneId
    || `scene-${item.number}` === sceneId
    || `scene-${String(item.number).padStart(2, "0")}` === sceneId
  );
  if (scene) return `Sc ${scene.number} · ${scene.slugline}`;
  const sceneNumber = sceneId.match(/^scene[-_](\d+)$/i)?.[1];
  return sceneNumber ? `Scene ${String(Number(sceneNumber)).padStart(2, "0")}` : sceneId || "Scene";
}

export function reviewGroupsForAnalysis(analysis: Record<string, unknown>, project: FilmProject): ReviewGroup[] {
  const scenes = project.scenes;
  const characters = project.characters;
  const locations = [...new Set(project.scenes.map((scene) => scene.location).filter(Boolean))];
  const voices = project.characters.filter((character) => character.voiceStatus !== "not_needed");
  const missing = rawMissingInputs(analysis, project);
  const classifiedMissing = missing.map((item) => ({ ...item, classification: `${item.label} ${item.kind}`.toLowerCase() }));
  const needsCharacter = classifiedMissing.filter((item) => item.classification.includes("character") || item.classification.includes("card")).length;
  const needsVoice = classifiedMissing.filter((item) => item.classification.includes("voice") || item.classification.includes("line")).length;
  const needsReference = classifiedMissing.filter((item) => item.classification.includes("reference") || item.classification.includes("location") || item.classification.includes("scene")).length;
  return [
    {
      id: "scenes",
      title: "Scenes",
      countLabel: `${scenes.length} detected`,
      detail: scenes.slice(0, 3).map((scene) => scene.slugline).join(" · ") || "No scenes detected yet",
      status: scenes.length ? "approved" : "needs_attention",
      badge: scenes.length ? "All approved" : "Needs script"
    },
    {
      id: "characters",
      title: "Characters",
      countLabel: `${characters.length} detected`,
      detail: characters.slice(0, 4).map((character) => character.name).join(" · ") || "No characters detected yet",
      status: needsCharacter ? "warning" : "approved",
      badge: needsCharacter ? `${needsCharacter} missing card${needsCharacter === 1 ? "" : "s"}` : "Cards ready"
    },
    {
      id: "locations",
      title: "Locations",
      countLabel: `${locations.length} detected`,
      detail: locations.slice(0, 4).join(" · ") || "No locations detected yet",
      status: needsReference ? "warning" : "approved",
      badge: needsReference ? `${needsReference} missing ref${needsReference === 1 ? "" : "s"}` : "Refs ready"
    },
    {
      id: "voices",
      title: "Voices",
      countLabel: `${voices.length} real speaker${voices.length === 1 ? "" : "s"}`,
      detail: voices.slice(0, 4).map((character) => character.name).join(" · ") || "No spoken speakers detected",
      status: needsVoice ? "warning" : "approved",
      badge: needsVoice ? `${needsVoice} missing audio` : "Voice plan ready"
    },
    {
      id: "look",
      title: "Visual look",
      countLabel: "Suggested",
      detail: `${project.stylePreset} · ${project.colorGrade}`,
      status: "approved",
      badge: "Edit look"
    },
    {
      id: "boards",
      title: "Board priorities",
      countLabel: "Auto-ranked",
      detail: project.agentBeats.length ? project.agentBeats.slice(0, 3).map((beat) => beat.label).join(" · ") : "Story order from parsed scenes",
      status: "approved",
      badge: "Review"
    }
  ];
}

export function missingInputsForAnalysis(analysis: Record<string, unknown>): MissingInput[] {
  return rawMissingInputs(analysis);
}

function characterForSceneMissing(project: FilmProject, scene: Scene, kind: "character" | "voice") {
  const sceneCharacterNames = new Set(scene.characters.map((name) => name.toLowerCase()));
  return project.characters.find((character) => {
    if (!sceneCharacterNames.has(character.name.toLowerCase())) return false;
    if (kind === "character") return character.cardStatus === "missing" || character.cardSource === "missing";
    return character.voiceStatus === "missing" || character.voiceSource === "missing";
  }) || project.characters.find((character) => sceneCharacterNames.has(character.name.toLowerCase()));
}

export function reviewMissingInputsForScenes(
  project: FilmProject,
  scenes: Scene[],
  options: { includeBoards?: boolean } = {}
): MissingInput[] {
  return scenes.flatMap((scene) => {
    const items: MissingInput[] = [];
    if (scene.readiness === "needs_character") {
      const character = characterForSceneMissing(project, scene, "character");
      items.push({
        id: `review-${scene.id}-character-card`,
        label: `${scene.slugline}: ${character ? `${character.name} character card` : "character card"}`,
        kind: "character_card",
        required: true,
        sceneId: scene.id,
        characterName: character?.name,
        fixRoute: "characters",
        focusId: character?.id || scene.id
      });
    }
    if (scene.readiness === "needs_voice") {
      const character = characterForSceneMissing(project, scene, "voice");
      items.push({
        id: `review-${scene.id}-voice`,
        label: `${scene.slugline}: ${character ? `${character.name} voice` : "voice"}`,
        kind: "voice_audio",
        required: true,
        sceneId: scene.id,
        characterName: character?.name,
        fixRoute: "voices",
        focusId: character?.id || scene.id
      });
    }
    if (scene.readiness === "missing_reference") {
      items.push({
        id: `review-${scene.id}-reference`,
        label: `${scene.slugline}: scene reference`,
        kind: "scene_reference",
        required: true,
        sceneId: scene.id,
        fixRoute: "references",
        focusId: scene.id
      });
    }
    if (options.includeBoards) {
      const storyboardIssue = sceneStoryboardIssue(scene);
      if (storyboardIssue) {
        items.push({
          id: `review-${scene.id}-storyboard-boards`,
          label: `${scene.slugline}: storyboard boards (${storyboardIssue})`,
          kind: "storyboard_frame",
          required: true,
          sceneId: scene.id,
          fixRoute: "generate_boards",
          focusId: scene.id
        });
      }
    }
    return items;
  });
}

export function missingInputRouteLabel(route?: MissingInputFixRoute): string {
  if (route === "characters") return "Characters";
  if (route === "voices") return "Voices";
  if (route === "references") return "References";
  if (route === "generate_boards") return "Generate Boards";
  return "Source";
}

export function missingInputToPackageRequired(item: MissingInput): PackageMissingRequired {
  return {
    category: "source",
    label: item.label,
    kind: item.kind || "source",
    reason: "Scene readiness requires this source before provider packaging.",
    sceneId: item.sceneId,
    fixRoute: item.fixRoute,
    focusId: item.focusId
  };
}

export function sceneReadinessCounts(scenes: Scene[]): { ready: number; needsVoice: number; needsCharacter: number; missingReference: number } {
  return {
    ready: scenes.filter((scene) => scene.readiness === "ready").length,
    needsVoice: scenes.filter((scene) => scene.readiness === "needs_voice").length,
    needsCharacter: scenes.filter((scene) => scene.readiness === "needs_character").length,
    missingReference: scenes.filter((scene) => scene.readiness === "missing_reference").length
  };
}

export function agentPlanSummary(analysis: Record<string, unknown>, project: FilmProject): AgentPlanSummary {
  const agentPlan = agentPlanRecord(analysis);
  const goal = asString(agentPlan.goal, project.goal) as ProjectGoal;
  const chunkPlan = isRecord(agentPlan.chunkPlan) ? agentPlan.chunkPlan : {};
  const runtimeSeconds = asNumber(
    chunkPlan.runtimeSeconds || agentPlan.runtimeSeconds || project.runtimeSeconds,
    project.runtimeSeconds
  );
  const chunkSeconds = Math.max(1, asNumber(chunkPlan.chunkSeconds, 10));
  const fallbackChunkCount = chunkCountForSeconds(runtimeSeconds, chunkSeconds);
  const beats: PlanBeat[] = asRecordArray(agentPlan.beats).map((beat, index) => {
    const sceneId = asString(beat.sceneId, project.agentBeats[index]?.sceneId || project.scenes[index % Math.max(1, project.scenes.length)]?.id || "");
    return {
      id: asString(beat.id, `beat-${index + 1}`),
      label: asString(beat.label || beat.type || beat.beatType, `Beat ${index + 1}`),
      sceneLabel: sceneLabelForId(project, sceneId),
      note: asString(beat.note || beat.purpose || beat.description, project.scenes[index]?.synopsis || "Production beat"),
      seconds: asNumber(beat.seconds || beat.durationSeconds, project.agentBeats[index]?.durationSeconds || 8),
      readiness: asString(beat.readiness) === "needs_input" ? "needs_input" : "ready"
    };
  });
  const fallbackBeats: PlanBeat[] = project.agentBeats.map((beat) => ({
    id: beat.id,
    label: beat.label,
    sceneLabel: sceneLabelForId(project, beat.sceneId),
    note: beat.purpose,
    seconds: beat.durationSeconds,
    readiness: beat.readiness
  }));
  const usableBeats = beats.length ? beats : fallbackBeats;
  const requestedChunkCount = asNumber(chunkPlan.chunkCount, fallbackChunkCount);
  const nextActions = asArray(agentPlan.nextActions).map(planActionFromValue).filter((item): item is PlanAction => Boolean(item));
  const approvalGates = asArray(agentPlan.approvalGates).map((item) => asString(item)).filter(Boolean);
  return {
    agentName: humanizeIdentifier(agentPlan.agent || agentPlan.name, "Kinetix Production Agent"),
    goal,
    confidence: Math.round(asNumber(agentPlan.confidence, 0.82) * 100),
    objective: asString(agentPlan.objective, `Prepare a ${goalLabel(goal).toLowerCase()} plan for ${project.title}.`),
    strategy: asString(agentPlan.strategy, "Use parsed scenes, uploaded assets, and missing-input checks before storyboard generation."),
    why: asString(agentPlan.why, "Highest visual density and emotional contrast per second. Remaining scenes stay parsed for alternate goals."),
    beats: usableBeats,
    missingInputs: rawMissingInputs(analysis, project),
    nextActions,
    approvalGates,
    runtimeSeconds,
    chunkSeconds,
    chunkCount: requestedChunkCount > 0 ? requestedChunkCount : fallbackChunkCount
  };
}

export function formatPlanRuntime(summary: AgentPlanSummary): string {
  return `${formatRuntime(summary.runtimeSeconds)} target · ${summary.beats.length} beats · ${summary.chunkCount} x ${summary.chunkSeconds}s chunks`;
}
